// Mock data for Emotion AI Research Platform

export const navigationLinks = [
  { id: 'home', label: 'Home' },
  { id: 'team', label: 'Domains' },
  { id: 'products', label: 'Products' },
  { id: 'demo', label: 'Demo' },
  { id: 'contact', label: 'Contact' }
];

// In-house AI product lineup — six engines built at Keimyung AI Research Center
export const aiProducts = [
  {
    id: 1,
    code: 'GRP',
    name: 'GrantPilot AI',
    tagline: 'ORCID-Matched Daily Grant Alerts',
    description:
      'Searches a professor’s ORCID record, learns their research signature, and delivers the most relevant funding calls to their inbox — every morning. From NRF and NIH to Horizon Europe, in a single curated email.',
    features: [
      'ORCID-based research profiling',
      'Daily personalized grant digest by email',
      'Auto-ranked by deadline & funding fit',
    ],
    status: 'Beta',
    accent: '#00BCD4',
    domain: 'Research Operations',
    cta: 'signup',
    waitlist: {
      productKey: 'grantpilot',
      title: 'Join GrantPilot AI',
      subtitle: 'ORCID-matched grant calls, delivered to your inbox every morning.',
      submitLabel: 'Join the waitlist',
      successTitle: 'You’re on the list',
      successBody: 'We’ve received your details. The team will reach out when GrantPilot AI opens for your profile.',
      orcidRequired: false,
    },
  },
  {
    id: 0,
    code: 'KAI',
    name: 'Kainos AI',
    tagline: 'The Center’s Foundation Language Model',
    description:
      'Keimyung’s own academic-grade large language model — fluent in Korean–English research discourse, grounded in peer-reviewed literature, and tuned for the citation discipline that science demands. The LLM service environment opens soon.',
    features: [
      'Korean ↔ English bilingual academic reasoning',
      'Citation-grounded responses from curated literature',
      'On-premise deployment for IRB-safe data',
    ],
    status: 'Preview',
    accent: '#111827',
    domain: 'Foundation Model',
  },
  {
    id: 2,
    code: 'TLN',
    name: 'TalentLens AI',
    tagline: 'Researcher & Candidate Analysis',
    description:
      'Multi-dimensional screening of academic and industry candidates — surfacing scholarly fit, collaboration patterns, and trajectory beyond the CV.',
    features: [
      'Publication & citation profiling',
      'Lab-fit & collaboration scoring',
      'Interview question generator',
    ],
    status: 'Alpha',
    accent: '#A78BFA',
    domain: 'People Intelligence',
  },
  {
    id: 3,
    code: 'PFG',
    name: 'ProposalForge AI',
    tagline: 'Research Proposal Authoring',
    description:
      'Turns rough ideas into structured research proposals — methodology, prior art, and hypothesis chains drafted in your lab’s voice and citation style.',
    features: [
      'Methodology & hypothesis scaffolding',
      'Prior-work synthesis with citations',
      'Korean ↔ English bilingual drafts',
    ],
    status: 'Live',
    accent: '#FF6B6B',
    domain: 'Scholarly Authoring',
  },
  {
    id: 4,
    code: 'CTC',
    name: 'CiteCompass AI',
    tagline: 'Literature Discovery & Citation Mapping',
    description:
      'Navigates 200M+ papers to build living citation maps — exposing the conceptual neighbors, contrarians, and seminal anchors of any research question.',
    features: [
      'Semantic literature search',
      'Citation graph visualization',
      'Auto-generated related-work sections',
    ],
    status: 'Beta',
    accent: '#4DD0E1',
    domain: 'Knowledge Discovery',
  },
  {
    id: 5,
    code: 'PRL',
    name: 'PeerLens AI',
    tagline: 'Peer Review & Manuscript Quality',
    description:
      'Pre-submission diagnostic for manuscripts — surfaces statistical pitfalls, missing controls, and reviewer-style critiques before the journal does.',
    features: [
      'Statistical rigor checks',
      'Reviewer-style critique simulation',
      'Venue & journal fit recommendations',
    ],
    status: 'Alpha',
    accent: '#81C784',
    domain: 'Editorial Intelligence',
  },
];

export const centerStats = [
  { value: "06", label: "Research Domains", note: "Interdisciplinary pillars" },
  { value: "24", label: "Faculty & PIs", note: "Across medicine, CS, humanities" },
  { value: "48+", label: "Publications", note: "Peer-reviewed · 2024 – 2026" },
  { value: "12", label: "Partner Institutes", note: "Domestic + international" },
  { value: "₩4.2B", label: "Active Grant Funding", note: "Multi-year programs" },
];

export const publications = [
  {
    id: 1,
    year: "2026",
    title: "Efficient Uncertainty-Aware Dual-Attention Network for Brain Tumor Detection",
    authors: "S. Afzal, J.-H. Lee",
    venue: "Mathematics · MDPI",
    type: "Journal",
    tag: "Biomedical AI",
    href: "https://www.mdpi.com/2227-7390/14/9/1421",
  },
  {
    id: 2,
    year: "2026",
    title: "A Cross-Modal Adaptive Gated Fusion Network for Facial Emotion Recognition",
    authors: "S. Afzal, J.-H. Lee",
    venue: "Mathematics · MDPI",
    type: "Journal",
    tag: "Emotion AI",
    href: "https://www.mdpi.com/2227-7390/14/6/1068",
  },
  {
    id: 3,
    year: "2026",
    title: "Simultaneous Analysis of Microsaccades and Pupil Size Variations in Age-Related Cognitive Impairment Using Eye-Tracking Technology",
    authors: "S. Oh, T. Nairuz, S.-J. Park, J.-H. Lee",
    venue: "Journal of Eye Movement Research",
    type: "Journal",
    tag: "Cognitive Neuroscience AI",
    href: "https://www.mdpi.com/1995-8692/19/2/29",
  },
  {
    id: 4,
    year: "2025",
    title: "Digital Staining Algorithm for Multi-Domain Transformation of Unstained Images",
    authors: "D.-B. Kim, J.-H. Lee",
    venue: "IEEE Access",
    type: "Journal",
    tag: "Biomedical AI",
    href: "https://ieeexplore.ieee.org/document/11008632",
  },
  {
    id: 5,
    year: "2025",
    title: "Photobiomodulation at 660 nm Alleviates Alzheimer's Disease Pathology Through Amyloid-β Reduction and SIRT1 Upregulation in the Hippocampus of 5xFAD Mice",
    authors: "T. Nairuz, J.-C. Heo, H.-J. Park, J.-H. Lee",
    venue: "International Journal of Molecular Sciences",
    type: "Journal",
    tag: "Cognitive Neuroscience AI",
    href: "https://doi.org/10.3390/ijms26199569",
  },
  {
    id: 6,
    year: "2025",
    title: "HRV-Based Recognition of Complex Emotions: Feature Identification and Emotion-Specific Indicator Selection",
    authors: "D.-Y. Kang, C.-I. Kim, J.-H. Lee",
    venue: "Healthcare · MDPI",
    type: "Journal",
    tag: "Emotion AI",
    href: "https://www.mdpi.com/2227-9032/13/23/3036",
  },
  {
    id: 7,
    year: "2024",
    title: "The Optical Parameter Optimization for Brain Implant Alzheimer Sensor Using Phototherapy Angle and Wavelength Simulation (PAWS) Methodology",
    authors: "S.-H. Cho, C.-H. Won, C. H. Kim, J.-H. Lee",
    venue: "Sensors · MDPI",
    type: "Journal",
    tag: "Biomedical AI",
    href: "https://www.mdpi.com/1424-8220/24/22/7282",
  },
  {
    id: 8,
    year: "2024",
    title: "Differential Glial Response and Neurodegenerative Patterns in CA1, CA3 and DG Hippocampal Regions of 5XFAD Mice",
    authors: "T. Nairuz, J.-C. Heo, J.-H. Lee",
    venue: "International Journal of Molecular Sciences",
    type: "Journal",
    tag: "Cognitive Neuroscience AI",
    href: "https://www.mdpi.com/1422-0067/25/22/12156",
  },
  {
    id: 9,
    year: "2024",
    title: "The Novel EfficientNet Architecture-Based System and Algorithm to Predict Complex Human Emotions",
    authors: "M. Khomidov, J.-H. Lee",
    venue: "Algorithms · MDPI",
    type: "Journal",
    tag: "Emotion AI",
    href: "https://www.mdpi.com/1999-4893/17/7/285",
  },
  {
    id: 10,
    year: "2023",
    title: "Remote Bio Vision: Perfusion Imaging Based Non-Contact Autonomic Biosignal Measurement Algorithm",
    authors: "C.-I. Kim, E.-B. Park, C.-H. Won, J.-H. Lee",
    venue: "IEEE Sensors Journal",
    type: "Journal",
    tag: "Biomedical AI",
    href: "https://ieeexplore.ieee.org/document/10137424",
  },
  {
    id: 11,
    year: "2022",
    title: "The Development of a Web-Based App Employing Machine Learning for Delirium Prevention in Long-term Care Facilities in South Korea",
    authors: "K. J. Moon, C.-S. Son, J.-H. Lee, M. Park",
    venue: "BMC Medical Informatics and Decision Making",
    type: "Journal",
    tag: "Clinical AI",
    href: "https://pubmed.ncbi.nlm.nih.gov/35978303/",
  },
  {
    id: 12,
    year: "2021",
    title: "Computer-Aided Diagnosis Algorithm for Classification of Malignant Melanoma Using Deep Neural Networks",
    authors: "C.-I. Kim, S.-M. Hwang, E.-B. Park, C.-H. Won, J.-H. Lee",
    venue: "Sensors · MDPI",
    type: "Journal",
    tag: "Biomedical AI",
    href: "https://www.mdpi.com/1424-8220/21/16/5551",
  },
];

export const partners = {
  domestic: [
    { abbr: "SNU", name: "Seoul National University" },
    { abbr: "KAIST", name: "KAIST" },
    { abbr: "DGIST", name: "Daegu Gyeongbuk Institute" },
    { abbr: "POSTECH", name: "Pohang University of S&T" },
    { abbr: "MFDS", name: "식약처 · MFDS" },
    { abbr: "NRF", name: "한국연구재단 · NRF" },
  ],
  international: [
    { abbr: "MIT", name: "MIT Media Lab" },
    { abbr: "SU", name: "Stanford HAI" },
    { abbr: "EPFL", name: "EPFL · LTS4" },
    { abbr: "IBM", name: "T. J. Watson Research" },
    { abbr: "NYU", name: "NYU CATT" },
    { abbr: "UT", name: "University of Tokyo" },
    { abbr: "ETH", name: "ETH Zürich" },
    { abbr: "MPI", name: "Max Planck Institute" },
  ],
};

export const pressMentions = [
  { outlet: "Korea Herald", title: "계명대, Keimyung AI 연구센터 출범", date: "Jan 2026" },
  { outlet: "MIT Technology Review", title: "Korea's New Push into Affective Computing", date: "Dec 2025" },
  { outlet: "Nature Briefing", title: "fNIRS-AI Pipeline Detects Hidden Adolescent Distress", date: "Nov 2025" },
  { outlet: "KBS News", title: "감정 인식 AI, 청소년 정신건강 조기 진단에 활용", date: "Oct 2025" },
  { outlet: "IEEE Spectrum", title: "Emotion AI Goes Clinical: Six Frontiers from a Korean Lab", date: "Sep 2025" },
  { outlet: "Chosun Biz", title: "감정 AI로 청소년 우울증 조기 진단 도전", date: "Aug 2025" },
];

export const eventSeries = [
  {
    id: 1,
    code: "EAS",
    title: "Emotion AI Symposium",
    tagline: "Weekly research seminar",
    description: "Our flagship weekly seminar featuring researchers presenting cutting-edge work on affective computing, multimodal sentiment, and biomedical AI. Open to the Keimyung community and the broader research public.",
    frequency: "Every Friday · 4:00 PM KST",
    accent: "#00BCD4",
    icon: "Sparkles",
    mission: "To create a sustained weekly forum where researchers across departments stress-test new findings in affective computing, biomedical AI, and multimodal learning — before they ever reach a conference room.",
    format: "60-minute talk · 30-minute open Q&A · post-seminar reading group",
    audience: "Faculty, graduate students, visiting scholars, and the public",
    organizers: "Dr. Eun-Bin Park · Prof. Jong-Ha Lee",
    venue: "Keimyung University · Bldg. AI-101 (also broadcast on Zoom)",
    contact: "symposium@k-airc.org",
    highlights: [
      "Over 80 talks since launch",
      "Hybrid format with full archive of recordings",
      "Reading group meets the following Monday"
    ]
  },
  {
    id: 2,
    code: "CDX",
    title: "Convergence Dialogues",
    tagline: "Where AI meets the humanities",
    description: "An intimate conversation series bringing philosophers, ethicists, artists, and computer scientists into the same room to wrestle with what it means for machines to understand. Quarterly invited talks with reading group.",
    frequency: "Quarterly · invited speakers",
    accent: "#A78BFA",
    icon: "BookOpen",
    mission: "To slow the conversation down. We invite humanists, artists, and engineers to share one stage — and to disagree productively about what it means to build, deploy, and live with machine intelligence.",
    format: "Two invited speakers in dialogue · moderated discussion · public Q&A · reception",
    audience: "Cross-disciplinary scholars, students, journalists, and citizens",
    organizers: "Prof. Hye-Rim Jang · Prof. Dong-Hyun Oh",
    venue: "Keimyung University · Convergence Hall (and webinar)",
    contact: "dialogues@k-airc.org",
    highlights: [
      "Pre-circulated reading distributed two weeks in advance",
      "Edited transcripts published as a quarterly journal",
      "Invited speakers from philosophy, art, policy, and CS"
    ]
  },
  {
    id: 3,
    code: "OLD",
    title: "Open Lab Days",
    tagline: "Public demos, talks & launches",
    description: "Monthly public events — live demos of our research platforms, book launches, film screenings on AI's role in society, and hands-on workshops for students and citizens. Everyone is welcome.",
    frequency: "Monthly · open to the public",
    accent: "#FFB74D",
    icon: "Eye",
    mission: "Research belongs to the public that funds it. Open Lab Days lower the door of the laboratory and invite citizens, students, and families to see, touch, and question the AI we are building.",
    format: "Live demos · short talks · book / film launches · hands-on workshops",
    audience: "General public — students, families, citizens, no prior background required",
    organizers: "Prof. Yu-Jin Choi · Center Operations",
    venue: "Keimyung University · Main Hall + Lab spaces",
    contact: "openlab@k-airc.org",
    highlights: [
      "Free admission · accessible venue",
      "Translation between Korean and English provided",
      "Workshops for K–12 students and senior citizens"
    ]
  }
];

export const facultyProfiles = {
  "Prof. Jong-Ha Lee": {
    name: "Prof. Jong-Ha Lee",
    nameKr: "이종하 교수",
    title: "Professor · Chief Information Officer for AI",
    affiliation: "Keimyung University · College of Medicine",
    departments: "Premedical · Biomedical Engineering · Computer Engineering",
    photo: "/prof-jong-ha-lee.jpg",
    sourceUrl: "https://www.aicdlab.com/Faculty:Jong-Ha_Lee_(KR)",
    bio: "Prof. Jong-Ha Lee is the principal investigator of the Artificial Intelligence Computer Diagnosis (AICD) Lab at Keimyung University College of Medicine, and serves as the university's Chief Information Officer for AI. His research spans ultra-high-resolution optical cancer imaging, deep-learning-based computer-aided diagnosis, multi-channel biosignal wellness platforms, and wirelessly-charged implantable micro-sensors for Alzheimer's diagnosis and therapy.",
    education: [
      { degree: "Ph.D., Electrical & Computer Engineering", school: "Temple University, USA", years: "2006 – 2011", note: "Biophotonics · Next-gen AI · Optimization" },
      { degree: "M.S., Electrical & Computer Engineering", school: "New York University (NYU) Tandon, USA", years: "2003 – 2006", note: "Biomedical wireless systems" },
      { degree: "B.S., Electrical Engineering", school: "Inha University, Korea", years: "1996 – 2000" },
    ],
    career: [
      { role: "Professor · CIO for AI", org: "Keimyung University", years: "2026 – Present" },
      { role: "Professor · Vice Dean for Research, Industry-Academia Cooperation", org: "Keimyung University College of Medicine", years: "2024 – 2026" },
      { role: "Associate Professor", org: "Keimyung University", years: "2017 – 2024" },
      { role: "Assistant Professor", org: "Keimyung University", years: "2012 – 2017" },
      { role: "Senior Researcher · Big Data Analytics Lab", org: "Samsung Advanced Institute of Technology (SAIT)", years: "2011 – 2012" },
      { role: "Graduate Researcher · CSNAP Lab", org: "Temple University", years: "2006 – 2011" },
      { role: "Researcher · WICAT Group", org: "NYU Center for Advanced Technology (CATT)", years: "2004 – 2006" },
    ],
    interests: [
      "Ultra-high-resolution optical cancer imaging",
      "Deep-learning-based AI computer-aided diagnosis",
      "Multi-channel biosignal wellness platforms",
      "Implantable wirelessly chargeable micro-sensors (Alzheimer's)",
      "IoT big-data biosignal stream mining",
      "Heterogeneous bio-data integrated diagnostic platforms",
    ],
    honors: [
      "SPIE Newport Research Excellence Award (2011)",
      "IEEE Senior Member",
      "Lifetime Member · Korea Information Science Society (KIISE)",
      "Expert Committee · MFDS Medical Device Committee, Korea",
      "KSEA Scholar (2010) · NYU CATT Scholar (2006)",
    ],
    collaborations: {
      international: ["EPFL · LTS4 (Switzerland)", "IBM T. J. Watson Research Center (USA)", "New York University (USA)"],
      domestic: ["Seoul National University", "Yonsei University", "KAIST", "DGIST"],
    },
    contact: {
      email: "segeberg@keimyung.ai",
      phone: "053-580-8837",
      address: "1095 Dalgubeol-daero, Dalseo-gu, Daegu 42601, Republic of Korea · Keimyung University",
    },
  },
};

export const upcomingEvents = [
  {
    id: 1,
    date: "2026-03-06",
    timeRange: "16:00 – 17:30",
    type: "Symposium",
    seriesCode: "EAS",
    title: "fNIRS-Decoded Affect in Adolescent Depression",
    speaker: "Prof. Jong-Ha Lee",
    speakerAffiliation: "Dept. of Biomedical Engineering, Keimyung University",
    speakerBio: "Prof. Lee leads the Neuroimaging & BCI Lab, with a decade of work on fNIRS-based affective decoding and biomedical AI. He directs the Intelligent Medical Imaging Lab and co-chairs the Center's Cognitive Neuroscience AI program.",
    moderator: "Dr. Eun-Bin Park",
    location: "Keimyung U · Bldg. AI-101",
    format: "Hybrid · Zoom + In-person",
    registerUrl: "https://example.com/register/eas-2026-03-06",
    abstract: "Adolescent depression is notoriously hard to detect through self-report alone. We present a four-year cohort study using prefrontal fNIRS to decode latent affective states during a controlled film-viewing paradigm. Our cross-subject transformer reaches 0.81 AUROC for clinically validated depression labels and, more importantly, recovers an interpretable temporal signature that aligns with the canonical PANAS scale.",
    tags: ["fNIRS", "Affective Computing", "Clinical AI", "Adolescent Mental Health"]
  },
  {
    id: 2,
    date: "2026-03-20",
    timeRange: "19:00 – 20:30",
    type: "Dialogue",
    seriesCode: "CDX",
    title: "What Does a Machine Owe Us? — Confucian Ethics Meets Generative AI",
    speaker: "Prof. Hye-Rim Jang × Dr. Min-Soo Han",
    speakerAffiliation: "Philosophy × School of Medicine, Keimyung University",
    speakerBio: "Prof. Jang works at the intersection of Confucian moral philosophy and contemporary AI ethics. Dr. Han is a clinician-scientist building foundation models for radiology. Together they have co-authored on the obligations of medical AI toward patients and families.",
    moderator: "Prof. Dong-Hyun Oh",
    location: "Online · Zoom Webinar",
    format: "Public webinar",
    registerUrl: "https://example.com/register/cdx-2026-03-20",
    abstract: "Western AI ethics is dominated by consent, autonomy, and harm. But what does a relational, role-based moral tradition — like Korean Confucianism — demand of a generative system that speaks, comforts, and prescribes? In a dialogue between a philosopher and a clinical AI researcher, we examine duties of deference, restraint, and care that current frameworks fail to articulate.",
    tags: ["AI Ethics", "Philosophy", "Generative AI", "Confucian Studies"]
  },
  {
    id: 3,
    date: "2026-03-28",
    timeRange: "14:00 – 18:00",
    type: "Open Day",
    seriesCode: "OLD",
    title: "Lab Open Day — Emotion AI Demos, Book Launch & Student Showcase",
    speaker: "All Center Faculty",
    speakerAffiliation: "Keimyung AI Research Center",
    speakerBio: "An afternoon featuring our entire faculty roster, graduate students, and visiting researchers. No prior background required — bring your family.",
    moderator: null,
    location: "Keimyung U · Main Hall",
    format: "In-person · free entry",
    registerUrl: "https://example.com/register/old-2026-03-28",
    abstract: "Step inside the lab. Try our real-time emotion mirror, walk through a 360° tour of our facilities, meet the researchers behind a new book on AI and the humanities, and watch graduate students defend live posters. The afternoon closes with a roundtable on AI's role in Korean society.",
    tags: ["Public Event", "Demos", "Book Launch", "Student Showcase"]
  },
  {
    id: 4,
    date: "2026-04-10",
    timeRange: "16:00 – 17:30",
    type: "Symposium",
    seriesCode: "EAS",
    title: "Privacy-Preserving Federated Phenotyping across Korean Hospitals",
    speaker: "Dr. Min-Soo Han",
    speakerAffiliation: "School of Medicine, Keimyung University",
    speakerBio: "Dr. Han directs the Clinical Decision AI Lab and leads the national federated learning initiative across six Korean tertiary hospitals. His work centers on regulatory-grade radiology AI.",
    moderator: "Dr. Chan-Il Kim",
    location: "Keimyung U · Bldg. AI-101",
    format: "Hybrid · Zoom + In-person",
    registerUrl: "https://example.com/register/eas-2026-04-10",
    abstract: "Hospitals cannot share patient data, yet rare diseases demand joint statistical power. We describe a production federated phenotyping system spanning six tertiary Korean hospitals, including differential privacy guarantees, secure aggregation, and the engineering trade-offs we had to accept on the way to IRB approval.",
    tags: ["Federated Learning", "Healthcare AI", "Privacy", "Clinical Deployment"]
  }
];

export const pastEvents = [
  {
    id: 101,
    date: "2026-01-23",
    type: "Symposium",
    seriesCode: "EAS",
    title: "Cross-Cultural Facial Expression Embeddings: A 12,000-Subject Study",
    speaker: "Dr. Eun-Bin Park"
  },
  {
    id: 102,
    date: "2025-12-12",
    type: "Symposium",
    seriesCode: "EAS",
    title: "Empathic Response Generation with Affect-Calibrated LLMs",
    speaker: "Prof. Jong-Ha Lee"
  },
  {
    id: 103,
    date: "2025-12-05",
    type: "Dialogue",
    seriesCode: "CDX",
    title: "Whose Common Sense? Cultural Bias in Foundation Models",
    speaker: "Prof. Hye-Rim Jang × Dr. Chan-Il Kim"
  },
  {
    id: 104,
    date: "2025-11-14",
    type: "Open Day",
    seriesCode: "OLD",
    title: "Autumn Open Day — Brain-Computer Interface Demos",
    speaker: "Neuroimaging & BCI Lab"
  },
  {
    id: 105,
    date: "2025-10-31",
    type: "Symposium",
    seriesCode: "EAS",
    title: "Predictive Coding Networks for Few-Shot Visual Learning",
    speaker: "Dr. Soo-Yeon Bae"
  },
  {
    id: 106,
    date: "2025-09-18",
    type: "Dialogue",
    seriesCode: "CDX",
    title: "Narrative Coherence as a Benchmark for LLM Understanding",
    speaker: "Prof. Dong-Hyun Oh × Dr. Eun-Bin Park"
  }
];

export const emotionExamples = [
  {
    id: 1,
    text: "I'm absolutely thrilled about this amazing opportunity! This is the best news I've heard all year.",
    emotion: "Excitement"
  },
  {
    id: 2,
    text: "I'm really worried about the presentation tomorrow. What if I mess up and everyone judges me?",
    emotion: "Anxiety"
  },
  {
    id: 3,
    text: "This traffic is absolutely infuriating! I can't believe I'm stuck here again for the third time this week.",
    emotion: "Frustration"
  }
];

export const researchProjects = [
  {
    id: 1,
    title: "Real-time Facial Emotion Recognition",
    description: "Advanced deep learning models for detecting and analyzing micro-expressions in real-time video streams with 97% accuracy.",
    status: "Active"
  },
  {
    id: 2,
    title: "Multimodal Emotion Detection",
    description: "Combining text, voice, and visual cues to create comprehensive emotion understanding systems for enhanced AI interactions.",
    status: "Active"
  },
  {
    id: 3,
    title: "Emotion Prediction in Healthcare",
    description: "Developing AI systems to predict emotional states in clinical settings to improve mental health diagnostics and patient care.",
    status: "In Development"
  },
  {
    id: 4,
    title: "Cross-cultural Emotion Analysis",
    description: "Understanding how emotions are expressed and perceived across different cultures to build more inclusive AI systems.",
    status: "Active"
  }
];

export const researchDomains = [
  {
    id: 1,
    code: "BIO",
    title: "Biomedical AI",
    tagline: "Precision Diagnostics & Clinical Intelligence",
    description: "Deep learning for medical imaging, disease prediction, and clinical decision support — transforming healthcare with explainable, patient-safe AI.",
    image: "/domains/bio.jpg",
    accent: "#00BCD4",
    icon: "Activity",
    overview: "Our Biomedical AI program develops clinically validated deep learning systems for radiology, pathology, and longitudinal patient monitoring. We focus on explainability, distribution shift, and regulatory readiness so that models trained in the lab survive bedside reality.",
    stats: [
      { label: "Active Projects", value: "12" },
      { label: "Publications", value: "48" },
      { label: "Clinical Partners", value: "6" }
    ],
    faculty: [
      { name: "Prof. Jong-Ha Lee", role: "Principal Investigator", affiliation: "Dept. of Biomedical Engineering", expertise: "Medical Imaging · Intelligent CV" },
      { name: "Prof. Jung-Hoon Koo", role: "Co-Investigator", affiliation: "Dept. of Biomedical Engineering · 의용공학과", expertise: "Biomedical Signal Processing · Wearable Sensing" }
    ],
    labs: [
      { name: "AICD Lab", fullName: "Artificial Intelligence Computer Diagnosis", lead: "Prof. Jong-Ha Lee", focus: "Optical cancer imaging · Deep-learning CAD · Biosignal wellness platforms · Implantable Alzheimer sensors", href: "https://www.aicdlab.com" }
    ],
    papers: [
      { title: "Self-Supervised Pre-training for Low-Dose CT Denoising", authors: "Lee J.H., Park E.B., et al.", venue: "Medical Image Analysis", year: 2025 },
      { title: "Explainable Foundation Models for Multi-Organ Segmentation", authors: "Han M.S., Lee J.H.", venue: "IEEE TMI", year: 2024 },
      { title: "Federated Learning across Korean Tertiary Hospitals", authors: "Lee J.H., et al.", venue: "Nature Digital Medicine", year: 2024 }
    ],
    projects: [
      { title: "AI-CAD for Early Lung Cancer Screening", status: "Active", period: "2024 – 2027", funding: "MOHW" },
      { title: "Multimodal Sepsis Early Warning System", status: "Active", period: "2025 – 2026", funding: "NRF" },
      { title: "Pediatric Bone-Age Estimation Toolkit", status: "Deployed", period: "2022 – 2024", funding: "Industry" }
    ]
  },
  {
    id: 2,
    code: "EMO",
    title: "Emotion AI",
    tagline: "Affective Computing & Empathic Interfaces",
    description: "Multimodal sentiment understanding across face, voice, and language — building machines that recognize and respond to human feelings.",
    image: "/domains/emo.jpg",
    accent: "#FF6B6B",
    icon: "HeartPulse",
    overview: "We model human affect as a continuous, contextual, and culturally situated signal. Our work fuses facial micro-expression analysis, prosodic speech features, and linguistic sentiment into agents that genuinely understand — and adapt to — emotional context.",
    stats: [
      { label: "Active Projects", value: "9" },
      { label: "Publications", value: "37" },
      { label: "Open Datasets", value: "4" }
    ],
    faculty: [
      { name: "Dr. Eun-Bin Park", role: "Director of Research", affiliation: "Keimyung AI Research Center", expertise: "Machine Learning · Affective NN" },
      { name: "Prof. Jong-Ha Lee", role: "Co-Investigator", affiliation: "Dept. of Biomedical Engineering", expertise: "Multimodal Vision" }
    ],
    labs: [
      { name: "Affective Computing Lab (ACL)", focus: "Face, voice, text fusion · empathic dialog agents", lead: "Dr. Eun-Bin Park" },
      { name: "Multimodal Interaction Lab", focus: "Real-time emotion-aware UX, wearable sensors", lead: "Prof. Jong-Ha Lee" }
    ],
    papers: [
      { title: "Cross-Cultural Facial Expression Embedding for Korean & Global Subjects", authors: "Park E.B., Kim C.I.", venue: "ACM ICMI", year: 2025 },
      { title: "Prosody-Conditioned Sentiment Transformer for Conversational AI", authors: "Park E.B., et al.", venue: "Interspeech", year: 2024 },
      { title: "Empathic Response Generation with Affect-Calibrated LLMs", authors: "Lee J.H., Park E.B.", venue: "EMNLP", year: 2024 }
    ],
    projects: [
      { title: "Real-Time Emotion Mirror for Telehealth Counseling", status: "Active", period: "2024 – 2026", funding: "NRF" },
      { title: "Affective Companion for Elderly Care", status: "Active", period: "2025 – 2027", funding: "MSIT" },
      { title: "Multimodal Mental-Health Triage Platform", status: "Pilot", period: "2025", funding: "Industry" }
    ]
  },
  {
    id: 3,
    code: "DAT",
    title: "Big Data Insights",
    tagline: "Predictive Intelligence at Scale",
    description: "Large-scale analytics and knowledge discovery on heterogeneous data — turning petabytes into actionable scientific and social insight.",
    image: "/domains/dat.jpg",
    accent: "#FFB74D",
    icon: "BarChart3",
    overview: "From population-scale health records to streaming sensor data, we design scalable representation learning and causal inference pipelines that surface patterns humans alone cannot. Our infrastructure stack is engineered for reproducibility, privacy, and real-time decisioning.",
    stats: [
      { label: "Active Projects", value: "8" },
      { label: "Publications", value: "29" },
      { label: "Data Partners", value: "11" }
    ],
    faculty: [
      { name: "Prof. Yo-Han Kim", role: "Principal Investigator", affiliation: "Dept. of Computer Engineering, Keimyung University", expertise: "Big Data Systems · Distributed ML · Knowledge Discovery" }
    ],
    labs: [
      { name: "Scalable Analytics Lab", focus: "Distributed ML, vector databases, retrieval systems", lead: "Prof. Yo-Han Kim" },
      { name: "Causal Data Science Lab", focus: "Treatment effects, observational health data", lead: "Prof. Yo-Han Kim" }
    ],
    papers: [
      { title: "Privacy-Preserving Population Phenotyping via Federated Embeddings", authors: "Kim Y.H., et al.", venue: "KDD", year: 2025 },
      { title: "Scalable Retrieval-Augmented Pipelines for Heterogeneous Health Data", authors: "Kim Y.H., et al.", venue: "JAMIA", year: 2024 },
      { title: "Streaming Anomaly Detection in IoT Healthcare Sensors", authors: "Kim Y.H., et al.", venue: "IEEE BigData", year: 2024 }
    ],
    projects: [
      { title: "Korean National Health Data Lakehouse", status: "Active", period: "2023 – 2027", funding: "MOHW" },
      { title: "City-Scale Mobility & Wellbeing Dashboard", status: "Active", period: "2024 – 2026", funding: "Municipal" },
      { title: "LLM-Powered Scientific Literature Synthesis", status: "Pilot", period: "2025", funding: "Internal" }
    ]
  },
  {
    id: 4,
    code: "CON",
    title: "Convergence AI",
    tagline: "Humanities · Philosophy · Ethics",
    description: "Bridging artificial intelligence with the humanities — philosophical foundations, ethics, narrative reasoning, and the nature of intelligence itself.",
    image: "/domains/con.jpg",
    accent: "#A78BFA",
    icon: "BookOpen",
    overview: "We ask the questions that pure engineering cannot: What does it mean for a machine to understand? Whose values does an AI encode? Our convergence agenda brings philosophers, literary scholars, and ethicists into the model design loop — producing AI that is technically sound and humanistically informed.",
    stats: [
      { label: "Active Projects", value: "6" },
      { label: "Publications", value: "21" },
      { label: "Cross-Faculty", value: "5 depts" }
    ],
    faculty: [
      { name: "Prof. Hye-Rim Jang", role: "Principal Investigator", affiliation: "Dept. of Philosophy", expertise: "Philosophy of Mind · AI Ethics" },
      { name: "Prof. Dong-Hyun Oh", role: "Co-Investigator", affiliation: "Dept. of Korean Literature", expertise: "Narrative Theory · Computational Humanities" }
    ],
    labs: [
      { name: "AI Ethics & Society Lab", focus: "Algorithmic accountability, value alignment", lead: "Prof. Hye-Rim Jang" },
      { name: "Computational Humanities Lab", focus: "LLMs for classical text, narrative generation", lead: "Prof. Dong-Hyun Oh" }
    ],
    papers: [
      { title: "Toward a Confucian Ethics of Conversational AI", authors: "Jang H.R.", venue: "AI & Society", year: 2025 },
      { title: "Narrative Coherence as a Benchmark for LLM Understanding", authors: "Oh D.H., Jang H.R.", venue: "Digital Scholarship in the Humanities", year: 2024 },
      { title: "Whose Common Sense? Cultural Bias in Foundation Models", authors: "Jang H.R., et al.", venue: "FAccT", year: 2024 }
    ],
    projects: [
      { title: "Korean AI Ethics Charter Drafting Initiative", status: "Active", period: "2024 – 2026", funding: "MCST" },
      { title: "Hanja-LLM: A Foundation Model for Classical East-Asian Texts", status: "Active", period: "2025 – 2027", funding: "NRF" },
      { title: "Public Deliberation on Generative AI", status: "Ongoing", period: "2024 – present", funding: "Internal" }
    ]
  },
  {
    id: 5,
    code: "NEU",
    title: "Cognitive Neuroscience AI",
    tagline: "Brain-Inspired Computing & fNIRS",
    description: "Decoding cognition through fNIRS, EEG, and neural signal modeling — engineering brain-aligned AI rooted in human perception and learning.",
    image: "/domains/neu.jpg",
    accent: "#4DD0E1",
    icon: "Brain",
    overview: "We treat the brain as both inspiration and instrument. Using fNIRS, EEG, and behavioral paradigms, we decode attention, emotion, and decision-making — then translate those insights into neural architectures that learn more like humans do.",
    stats: [
      { label: "Active Projects", value: "7" },
      { label: "Publications", value: "32" },
      { label: "BCI Studies", value: "14" }
    ],
    faculty: [
      { name: "Prof. Jong-Ha Lee", role: "Principal Investigator", affiliation: "Dept. of Biomedical Engineering", expertise: "fNIRS · Neural Decoding" },
      { name: "Dr. Soo-Yeon Bae", role: "Research Professor", affiliation: "Cognitive Science Program", expertise: "Attention · Working Memory" }
    ],
    labs: [
      { name: "Neuroimaging & BCI Lab", focus: "fNIRS / EEG signal processing, brain-computer interfaces", lead: "Prof. Jong-Ha Lee" },
      { name: "Cognitive Computation Lab", focus: "Predictive coding, biologically-plausible learning", lead: "Dr. Soo-Yeon Bae" }
    ],
    papers: [
      { title: "Real-Time Emotion Decoding from fNIRS Prefrontal Signals", authors: "Lee J.H., Bae S.Y.", venue: "NeuroImage", year: 2025 },
      { title: "Predictive Coding Networks for Few-Shot Visual Learning", authors: "Bae S.Y., et al.", venue: "NeurIPS", year: 2024 },
      { title: "Cross-Subject EEG Alignment via Contrastive Self-Supervision", authors: "Lee J.H., et al.", venue: "IEEE TNSRE", year: 2024 }
    ],
    projects: [
      { title: "fNIRS-Based Affective State Monitor for Clinical Use", status: "Active", period: "2024 – 2026", funding: "NRF" },
      { title: "Brain-Aligned Vision Foundation Model", status: "Active", period: "2025 – 2028", funding: "MSIT" },
      { title: "BCI for Motor-Impaired Rehabilitation", status: "Pilot", period: "2025", funding: "Industry" }
    ]
  },
  {
    id: 6,
    code: "HCI",
    title: "Human-Centered AI",
    tagline: "Responsible Design & Human-AI Teaming",
    description: "Crafting AI systems that are accessible, transparent, and aligned with human values — from interaction design to algorithmic fairness.",
    image: "/domains/hci.jpg",
    accent: "#81C784",
    icon: "Users",
    overview: "Algorithms only matter when humans can trust, understand, and shape them. We co-design AI with end users — clinicians, students, citizens — and evaluate it in the wild, measuring usefulness, fairness, and long-term wellbeing rather than benchmarks alone.",
    stats: [
      { label: "Active Projects", value: "10" },
      { label: "Publications", value: "26" },
      { label: "Field Deployments", value: "5" }
    ],
    faculty: [
      { name: "Dr. Eun-Bin Park", role: "Co-Investigator", affiliation: "Keimyung AI Research Center", expertise: "Interaction Design · Trust in AI" },
      { name: "Prof. Yu-Jin Choi", role: "Principal Investigator", affiliation: "Dept. of HCI & Design", expertise: "Participatory Design · Accessibility" }
    ],
    labs: [
      { name: "Human-AI Interaction Lab", focus: "Trust calibration, explainability UI, human-in-the-loop", lead: "Prof. Yu-Jin Choi" },
      { name: "Inclusive AI Lab", focus: "Accessibility, fairness audits, participatory ML", lead: "Dr. Eun-Bin Park" }
    ],
    papers: [
      { title: "When Should AI Defer? Designing Calibrated Hand-Off in Clinical Decision Support", authors: "Choi Y.J., Park E.B.", venue: "ACM CHI", year: 2025 },
      { title: "Accessibility-First Generative Interfaces for Low-Vision Users", authors: "Choi Y.J., et al.", venue: "ASSETS", year: 2024 },
      { title: "Auditing Bias in Korean Voice Assistants", authors: "Park E.B., Choi Y.J.", venue: "FAccT", year: 2024 }
    ],
    projects: [
      { title: "Trust-Calibrated AI Copilot for Radiologists", status: "Active", period: "2024 – 2026", funding: "MOHW" },
      { title: "Universal-Design Generative UI Toolkit", status: "Active", period: "2025 – 2027", funding: "MSIT" },
      { title: "Citizen Panel for Responsible AI Deployment", status: "Ongoing", period: "2024 – present", funding: "Internal" }
    ]
  }
];

export const mockAnalyzeText = (text) => {
  // Simulate API response
  const emotions = ['Joy', 'Sadness', 'Anger', 'Fear', 'Surprise', 'Disgust'];
  const randomEmotion = emotions[Math.floor(Math.random() * emotions.length)];
  const confidence = (Math.random() * 30 + 70).toFixed(2);
  
  return {
    primaryEmotion: randomEmotion,
    confidence: confidence,
    emotionScores: {
      Joy: Math.random() * 100,
      Sadness: Math.random() * 100,
      Anger: Math.random() * 100,
      Fear: Math.random() * 100,
      Surprise: Math.random() * 100,
      Disgust: Math.random() * 100
    }
  };
};

export const mockAnalyzeImage = (file) => {
  // Simulate image analysis
  const emotions = ['Happy', 'Sad', 'Neutral', 'Surprised', 'Angry'];
  const randomEmotion = emotions[Math.floor(Math.random() * emotions.length)];
  
  return {
    detectedEmotion: randomEmotion,
    confidence: (Math.random() * 25 + 75).toFixed(2),
    faceDetected: true
  };
};